import { useEffect, useState, useCallback, createContext, useContext, useRef } from "react";
import { loadFromCloud, saveToCloud, saveKeysToCloud, installSyncInterceptor, getSyncStatus, flushPendingChanges, forceSyncAll, prettySyncKey, setConflictCallback, discardPendingKey, isCurrentUserTimedOut, currentUserHasBypass, isCurrentUserOwner, type ConflictInfo } from "@/data/cloudSync";
import { useTenantStore } from "@/lib/tenantContext";
import { updateLastSeen } from "@/data/adminControlsStore";
import { addActivityEntry } from "@/data/activityLogStore";
import { supabase } from "@/integrations/supabase/client";
import { Cloud, RefreshCw, Save, Check, Loader2, AlertTriangle, ChevronDown, ChevronRight, Clock, X, List } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";

interface DeferredConflict {
  info: ConflictInfo;
  label: string;
  deferredAt: number;
}

/** Expiry options for conflict auto-dismiss */
export const CONFLICT_EXPIRY_OPTIONS = [
  { label: "6 hours", ms: 6 * 60 * 60 * 1000 },
  { label: "12 hours", ms: 12 * 60 * 60 * 1000 },
  { label: "24 hours", ms: 24 * 60 * 60 * 1000 },
  { label: "48 hours", ms: 48 * 60 * 60 * 1000 },
  { label: "7 days", ms: 7 * 24 * 60 * 60 * 1000 },
] as const;

const EXPIRY_STORAGE_KEY = "khays_conflict_expiry_ms";

function getConflictExpiryMs(): number {
  try {
    const stored = localStorage.getItem(EXPIRY_STORAGE_KEY);
    if (stored) return parseInt(stored, 10);
  } catch {}
  return 24 * 60 * 60 * 1000;
}

export function setConflictExpiryMs(ms: number): void {
  localStorage.setItem(EXPIRY_STORAGE_KEY, String(ms));
}

export function getConflictExpiryMsValue(): number {
  return getConflictExpiryMs();
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

interface RealtimeDiagnostics {
  status: "connected" | "disconnected" | "reconnecting";
  lastEventTime: number | null;
  reconnectCount: number;
  fallbackPollActive: boolean;
  lastPollTime: number | null;
}

interface SyncContextValue {
  isLoaded: boolean;
  isSyncing: boolean;
  lastSyncTime: number;
  pendingCount: number;
  pendingKeys: string[];
  hasPendingChanges: boolean;
  isTimedOut: boolean;
  hasBypass: boolean;
  isOwner: boolean;
  manualSave: () => Promise<void>;
  manualLoad: () => Promise<void>;
  pushPendingKeys: (keys: string[]) => Promise<void>;
  discardPendingKeys: (keys: string[]) => Promise<void>;
  deferredConflicts: DeferredConflict[];
  reviewDeferredConflict: (index: number) => void;
  removeDeferredConflict: (index: number) => void;
  dismissAllDeferred: () => void;
  realtimeDiagnostics: RealtimeDiagnostics;
}

const SyncContext = createContext<SyncContextValue>({
  isLoaded: false,
  isSyncing: false,
  lastSyncTime: 0,
  pendingCount: 0,
  pendingKeys: [],
  hasPendingChanges: false,
  isTimedOut: false,
  hasBypass: false,
  isOwner: false,
  manualSave: async () => {},
  manualLoad: async () => {},
  pushPendingKeys: async () => {},
  discardPendingKeys: async () => {},
  deferredConflicts: [],
  reviewDeferredConflict: () => {},
  removeDeferredConflict: () => {},
  dismissAllDeferred: () => {},
  realtimeDiagnostics: { status: "disconnected", lastEventTime: null, reconnectCount: 0, fallbackPollActive: false, lastPollTime: null },
});

export const useSync = () => useContext(SyncContext);

/** Human-readable label for a sync key */
const friendlyKeyName = (key: string) =>
  key.replace(/^khays_/, "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

/** Brief description of what a sync key controls */
const keyDescription = (key: string): string => {
  const map: Record<string, string> = {
    khays_admin_settings: "Pricing, margins, commission rates, and general admin configuration",
    khays_storefront_settings: "Storefront visibility, lockdown status, and display preferences",
    khays_orders: "Customer orders and order history",
    khays_stock: "Device stock quantities and availability",
    khays_device_settings: "Device visibility toggles, enabled/disabled states",
    khays_removed_devices: "List of removed devices from the catalog",
    khays_shipping_options: "Shipping methods and delivery options",
    khays_shipping_category_assignments: "Category-to-shipping-option assignments",
    khays_about: "About page content and store information",
    khays_reviews: "Customer reviews and ratings",
    khays_faqs: "Frequently asked questions content",
    khays_flash_sale: "Flash sale configuration and countdown settings",
    khays_ad_slots: "Advertisement slot configuration",
    khays_analytics: "Analytics and tracking data",
    khays_featured_config: "Featured device selection and display settings",
    khays_device_links: "External links associated with devices",
    khays_media: "Media gallery images and files",
    khays_category_settings: "Category visibility and ordering preferences",
    khays_custom_sub_variants: "Custom sub-variant definitions",
    khays_swap_settings: "Swap/trade-in program settings",
    khays_swap_pricing: "Swap pricing rules and values",
    khays_tracking_settings: "Order tracking configuration",
    khays_menu_visibility: "Admin menu visibility preferences",
    khays_settings_items_visibility: "Storefront Settings tab item visibility",
    khays_enquiries: "Customer enquiries and messages",
    khays_auto_pricing: "Auto-pricing rules and schedules",
    khays_permissions_config: "Staff permission and access settings",
    khays_message_templates: "Message and notification templates",
    khays_condition_guide: "Device condition grading guide",
    khays_recent_devices: "Recently viewed devices for storefront search",
    khays_tutorial_video: "Tutorial video widget configuration",
  };
  return map[key] || "Application settings and data";
};

export function CloudSyncProvider({ children }: { children: React.ReactNode }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingKeys, setPendingKeys] = useState<string[]>([]);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [hasBypass, setHasBypass] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [conflict, setConflict] = useState<ConflictInfo | null>(null);
  const [conflictQueue, setConflictQueue] = useState<ConflictInfo[]>([]);
  const [deferredConflicts, setDeferredConflicts] = useState<DeferredConflict[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  const conflictCloseModeRef = useRef<"overwrite" | "reload" | "defer" | null>(null);
  const [rtDiag, setRtDiag] = useState<RealtimeDiagnostics>({
    status: "disconnected", lastEventTime: null, reconnectCount: 0,
    fallbackPollActive: true, lastPollTime: null,
  });
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const tenantId = useTenantStore(s => s.tenantId);
  const tenantMode = !!tenantId;

  const isAdminRoute = useCallback(() => {
    const path = window.location.pathname;
    return path.startsWith("/admin") || path.startsWith("/owner");
  }, []);

  const refreshStatus = useCallback(() => {
    const status = getSyncStatus();
    setIsSyncing(status.isSyncing);
    setLastSync(status.lastSyncTime);
    setPendingCount(status.pendingCount);
    setPendingKeys(status.pendingKeys);
    setHasPendingChanges(status.hasPendingChanges);
  }, []);

  const logSyncAudit = useCallback(async (action: string, target: string, details: string) => {
    const { data } = await supabase.auth.getSession();
    addActivityEntry({
      username: data.session?.user.email || "Admin",
      action,
      target,
      details,
    });
  }, []);

  // When conflict opens, select all keys by default
  useEffect(() => {
    if (conflict) {
      setSelectedKeys(new Set(conflict.keys));
      setExpandedKeys(new Set());
    }
  }, [conflict]);

  // Auto-expire old deferred conflicts
  useEffect(() => {
    if (deferredConflicts.length === 0) return;
    const timer = setInterval(() => {
      const now = Date.now();
      setDeferredConflicts(prev => {
        const expiryMs = getConflictExpiryMs();
        const filtered = prev.filter(c => now - c.deferredAt < expiryMs);
        if (filtered.length < prev.length) {
          toast({ title: "Conflicts expired", description: `${prev.length - filtered.length} deferred conflict(s) auto-dismissed after expiry.` });
        }
        return filtered.length !== prev.length ? filtered : prev;
      });
    }, 60_000); // Check every minute
    return () => clearInterval(timer);
  }, [deferredConflicts.length]);

  // When current conflict is closed, show next from queue
  useEffect(() => {
    if (!conflict && conflictQueue.length > 0) {
      const [next, ...rest] = conflictQueue;
      setConflictQueue(rest);
      setConflict(next);
    }
  }, [conflict, conflictQueue]);

  useEffect(() => {
    setConflictCallback((info) => {
      if (tenantMode) {
        info.onReload();
        return;
      }
      if (isAdminRoute()) {
        // Stack: if a conflict dialog is already open, queue the new one
        setConflict(prev => {
          if (prev) {
            setConflictQueue(q => [...q, info]);
            return prev;
          }
          return info;
        });
      } else {
        info.onReload();
      }
    });
    return () => setConflictCallback(null);
  }, [isAdminRoute, tenantMode]);

  useEffect(() => {
    installSyncInterceptor();
    setIsLoaded(true);
    refreshStatus();
  }, [refreshStatus]);

  // Load cloud data AFTER auth state settles to avoid navigator lock contention
  useEffect(() => {
    if (!isLoaded) return;
    let mounted = true;
    let loaded = false;

    const doLoad = async () => {
      if (loaded) return;
      loaded = true;
      try {
        await Promise.race([
          loadFromCloud(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Cloud load timeout")), 4000)),
        ]);
      } catch (e) {
        console.warn("Initial cloud load failed or timed out, using local data:", e);
      }
      if (mounted) refreshStatus();
    };

    // Wait briefly for auth to settle before loading cloud data
    const timer = setTimeout(doLoad, 500);

    // Also listen for auth state to trigger load
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      setTimeout(doLoad, 200);
    });

    return () => {
      mounted = false;
      clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [isLoaded, refreshStatus]);

  useEffect(() => {
    if (!isLoaded) return;
    refreshStatus();
    // Poll every 1s — heavy storefront consumers no longer depend on
    // lastSyncTime (they use Zustand store revisions), so this only updates
    // the SyncBadge / pending count UI in the admin header.
    const timer = window.setInterval(refreshStatus, 1000);
    return () => window.clearInterval(timer);
  }, [isLoaded, refreshStatus]);

  // Periodically check timeout/bypass/owner status and update heartbeat
  useEffect(() => {
    if (!isLoaded) return;
    const check = async () => {
      setIsTimedOut(await isCurrentUserTimedOut());
      setHasBypass(await currentUserHasBypass());
      setIsOwner(await isCurrentUserOwner());
    };
    check();
    const timer = window.setInterval(check, 30000);
    return () => window.clearInterval(timer);
  }, [isLoaded]);

  // Realtime subscription — persistent, with reconnect tracking.
  // When a store_manager is signed in, switch the channel to listen on
  // `store_tenant_data` filtered by their tenant_id so they only receive
  // events for their own isolated container.
  useEffect(() => {
    if (!isLoaded) return;
    let reconnectCount = 0;

    const setupChannel = () => {
      // Clean up previous channel if any
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }

      const table = tenantId ? "store_tenant_data" : "app_data";
      const channelName = tenantId ? `store_tenant_data_${tenantId}` : "app_data_changes";
      const filter = tenantId ? `tenant_id=eq.${tenantId}` : undefined;

      const channel = supabase
        .channel(channelName, {
          config: { broadcast: { self: false } },
        })
        .on('postgres_changes', { event: '*', schema: 'public', table, ...(filter ? { filter } : {}) }, async (payload) => {
          setRtDiag(prev => ({ ...prev, lastEventTime: Date.now() }));
          const status = getSyncStatus();
          if (status.isSyncing) return;
          await new Promise(r => setTimeout(r, 300));
          await loadFromCloud();
          refreshStatus();
          // Storefront receives live updates silently — no toast needed
        })
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            setRtDiag(prev => ({ ...prev, status: "connected", reconnectCount }));
          } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setRtDiag(prev => ({ ...prev, status: "reconnecting" }));
            reconnectCount++;
            // Auto-reconnect after brief delay
            setTimeout(() => setupChannel(), 2000);
          } else if (status === 'CLOSED') {
            setRtDiag(prev => ({ ...prev, status: "disconnected" }));
          }
        });

      channelRef.current = channel;
    };

    setupChannel();

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [isLoaded, tenantId]); // re-subscribe when tenant scope changes

  // Fallback polling (slower interval since realtime handles most updates)
  useEffect(() => {
    if (!isLoaded) return;
    const poll = window.setInterval(async () => {
      const status = getSyncStatus();
      if (status.isSyncing) return;
      if (status.lastWriteTime && Date.now() - status.lastWriteTime < 3000) return;
      try {
        await loadFromCloud();
        refreshStatus();
        setRtDiag(prev => ({ ...prev, fallbackPollActive: true, lastPollTime: Date.now() }));
      } catch {}
    }, 30000);
    return () => window.clearInterval(poll);
  }, [isLoaded, refreshStatus]);

  const manualSave = useCallback(async () => {
    setIsSyncing(true);
    try {
      await flushPendingChanges();
      const result = await saveToCloud();
      refreshStatus();
      if (result.success) {
        await logSyncAudit("synced", "Cloud Sync", `Manual save pushed ${result.keysSaved} key(s)`);
        toast({ title: "Saved to cloud", description: `${result.keysSaved} settings synced successfully.` });
      } else {
        toast({ title: "Save failed", description: "Could not save to cloud. Please try again.", variant: "destructive" });
      }
    } finally { refreshStatus(); }
  }, [logSyncAudit, refreshStatus]);

  const manualLoad = useCallback(async () => {
    setIsSyncing(true);
    try {
      const result = await loadFromCloud();
      refreshStatus();
      if (result.success) {
        await logSyncAudit("loaded", "Cloud Sync", `Manual load pulled ${result.keysLoaded} key(s)`);
        toast({ title: "Loaded from cloud", description: `${result.keysLoaded} settings loaded successfully.` });
      } else {
        toast({ title: "Load failed", description: "Could not load from cloud.", variant: "destructive" });
      }
    } finally { refreshStatus(); }
  }, [logSyncAudit, refreshStatus]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      const status = getSyncStatus();
      if (status.hasPendingChanges) flushPendingChanges();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;
    const handleOnline = async () => {
      await flushPendingChanges();
      refreshStatus();
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [isLoaded, refreshStatus]);

  const handleOverwriteSelected = () => {
    if (!conflict) return;
    conflictCloseModeRef.current = "overwrite";
    const selected = Array.from(selectedKeys);
    if (selected.length === conflict.keys.length) {
      conflict.onOverwrite();
    } else {
      conflict.onOverwriteKeys(selected);
    }
    toast({ title: "Changes applied", description: `${selected.length} setting(s) pushed to cloud.` });
    setConflict(null);
  };

  const handleReloadAll = () => {
    if (!conflict) return;
    conflictCloseModeRef.current = "reload";
    conflict.onReload();
    toast({ title: "Reloaded", description: "Latest cloud data loaded. Your local changes were discarded." });
    setConflict(null);
  };

  const handleDefer = () => {
    if (!conflict) return;
    conflictCloseModeRef.current = "defer";
    conflict.onDefer();
    const label = conflict.keys.map(k => friendlyKeyName(k)).join(", ");
    setDeferredConflicts(prev => [...prev, { info: conflict, label, deferredAt: Date.now() }]);
    toast({ title: "Deferred", description: `${conflict.keys.length} conflicting change(s) saved for later.` });
    setConflict(null);
  };

  const handleReviewDeferred = (index: number) => {
    const item = deferredConflicts[index];
    setDeferredConflicts(prev => prev.filter((_, i) => i !== index));
    setConflict(item.info);
  };

  const handleDismissAllDeferred = () => {
    deferredConflicts.forEach(c => c.info.onDefer());
    setDeferredConflicts([]);
    toast({ title: "All dismissed", description: "Deferred conflicts cleared." });
  };

  const toggleKey = (key: string) => {
    setSelectedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleExpand = (key: string) => {
    setExpandedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAll = () => {
    if (!conflict) return;
    if (selectedKeys.size === conflict.keys.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(conflict.keys));
    }
  };

  const handleRemoveDeferred = (index: number) => {
    setDeferredConflicts(prev => prev.filter((_, i) => i !== index));
  };

  const handlePushPendingKeys = useCallback(async (keys: string[]) => {
    await saveKeysToCloud(keys);
    refreshStatus();
    await logSyncAudit("pushed", "Cloud Sync", `Pushed pending keys: ${keys.map(friendlyKeyName).join(", ")}`);
    toast({ title: "Pushed to cloud", description: `${keys.length} setting(s) synced.` });
  }, [logSyncAudit, refreshStatus]);

  const handleDiscardPendingKeys = useCallback(async (keys: string[]) => {
    for (const key of keys) {
      await discardPendingKey(key);
    }
    refreshStatus();
    await logSyncAudit("discarded", "Cloud Sync", `Discarded pending keys: ${keys.map(friendlyKeyName).join(", ")}`);
    toast({ title: "Discarded", description: `${keys.length} pending change(s) reverted to cloud version.` });
  }, [logSyncAudit, refreshStatus]);

  return (
    <SyncContext.Provider
      value={{ isLoaded, isSyncing, lastSyncTime: lastSync, pendingCount, pendingKeys, hasPendingChanges, isTimedOut, hasBypass, isOwner, manualSave, manualLoad, pushPendingKeys: handlePushPendingKeys, discardPendingKeys: handleDiscardPendingKeys, deferredConflicts, reviewDeferredConflict: handleReviewDeferred, removeDeferredConflict: handleRemoveDeferred, dismissAllDeferred: handleDismissAllDeferred, realtimeDiagnostics: rtDiag }}
    >
      {children}

      {!tenantMode && !isLoaded && (
        <div className="fixed bottom-4 left-4 z-50 rounded-full border border-border bg-card/95 px-3 py-2 shadow-lg backdrop-blur-sm">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />
            Syncing cloud data...
          </div>
        </div>
      )}

      {!tenantMode && <AlertDialog open={!!conflict} onOpenChange={(open) => {
        if (open) return;
        const closeMode = conflictCloseModeRef.current;
        conflictCloseModeRef.current = null;
        if (!closeMode && conflict) handleDefer();
      }}>
        <AlertDialogContent className="max-w-[95vw] sm:max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <AlertDialogHeader className="p-4 pb-2 shrink-0">
            <AlertDialogTitle className="flex items-center gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 text-destructive shrink-0" />
              Sync Conflict — {conflict?.keys.length} item{(conflict?.keys.length ?? 0) > 1 ? "s" : ""}
              {conflictQueue.length > 0 && (
                <span className="text-[10px] font-normal text-muted-foreground ml-auto">+{conflictQueue.length} more</span>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <p className="text-xs text-muted-foreground">
                Another admin modified these settings. Select which of your changes to keep.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>

          {/* Select all toggle */}
          <div className="px-4 py-1.5 border-b border-border flex items-center gap-2 shrink-0">
            <Checkbox
              checked={conflict ? selectedKeys.size === conflict.keys.length : false}
              onCheckedChange={toggleAll}
              className="h-3.5 w-3.5"
            />
            <span className="text-[11px] font-medium text-muted-foreground">
              {selectedKeys.size === (conflict?.keys.length ?? 0) ? "Deselect all" : "Select all"}
            </span>
            <span className="ml-auto text-[10px] text-muted-foreground">
              {selectedKeys.size} / {conflict?.keys.length ?? 0} selected
            </span>
          </div>

          {/* Scrollable conflict list */}
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-2 space-y-1">
              {conflict?.keys.map(key => {
                const isSelected = selectedKeys.has(key);
                const isExpanded = expandedKeys.has(key);
                const modifiedBy = conflict.updatedByMap?.[key];
                const modifiedAt = conflict.updatedAtMap?.[key];
                return (
                  <div
                    key={key}
                    className={`rounded-lg border transition-colors ${
                      isSelected ? "border-primary/40 bg-primary/5" : "border-border bg-card"
                    }`}
                  >
                    <div className="flex items-center gap-2 px-2.5 py-2">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleKey(key)}
                        className="h-3.5 w-3.5 shrink-0"
                      />
                      <button
                        onClick={() => toggleExpand(key)}
                        className="flex items-center gap-1 flex-1 min-w-0 text-left"
                      >
                        {isExpanded
                          ? <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0" />
                          : <ChevronRight className="w-3 h-3 text-muted-foreground shrink-0" />
                        }
                        <span className="text-xs font-medium text-foreground truncate">
                          {friendlyKeyName(key)}
                        </span>
                      </button>
                      {modifiedBy && (
                        <span className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded shrink-0">
                          by {modifiedBy}
                        </span>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="px-3 pb-2 pt-0 pl-8 space-y-1.5">
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          {keyDescription(key)}
                        </p>
                        {/* Change summaries */}
                        {conflict.changeSummaries?.[key]?.length > 0 && (
                          <div className="space-y-0.5 bg-muted/40 rounded-md p-1.5">
                            {conflict.changeSummaries[key].map((s, idx) => (
                              <p key={idx} className={`text-[10px] leading-snug ${s.startsWith("  →") ? "text-muted-foreground/70 pl-2" : "text-foreground/80 font-medium"}`}>
                                {s.startsWith("  →") ? s : `• ${s}`}
                              </p>
                            ))}
                          </div>
                        )}
                        {(modifiedBy || modifiedAt) && (
                          <p className="text-[10px] text-muted-foreground/70">
                            Changed{modifiedBy ? ` by ${modifiedBy}` : ""}{modifiedAt ? ` · ${new Date(modifiedAt).toLocaleString()}` : ""}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Actions */}
          <AlertDialogFooter className="p-3 pt-2 border-t border-border shrink-0 flex-row gap-1.5 sm:gap-1.5">
            <button
              onClick={handleDefer}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium border border-border bg-card text-muted-foreground hover:bg-muted transition-colors"
            >
              <Clock className="w-3 h-3" />
              Later
            </button>
            <button
              onClick={handleReloadAll}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium border border-border bg-card text-foreground hover:bg-muted transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              Discard All
            </button>
            <button
              onClick={handleOverwriteSelected}
              disabled={selectedKeys.size === 0}
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:pointer-events-none"
            >
              <Save className="w-3 h-3" />
              Apply {selectedKeys.size > 0 ? `(${selectedKeys.size})` : ""}
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>}
    </SyncContext.Provider>
  );
}
export function SyncButton() {
  const { isSyncing, manualSave, manualLoad, hasPendingChanges, pendingCount } = useSync();
  const [showMenu, setShowMenu] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isOnAdminPage, setIsOnAdminPage] = useState(false);
  const [forceSyncLog, setForceSyncLog] = useState<string[] | null>(null);
  const [showSyncLog, setShowSyncLog] = useState(true);

  const logSyncAudit = async (action: string, details: string) => {
    const { data } = await supabase.auth.getSession();
    addActivityEntry({ username: data.session?.user.email || "Admin", action, target: "Cloud Sync", details });
  };

  useEffect(() => {
    const checkLocation = () => setIsOnAdminPage(window.location.pathname === "/admin");
    checkLocation();
    window.addEventListener("popstate", checkLocation);
    const interval = window.setInterval(checkLocation, 1000);
    return () => { window.removeEventListener("popstate", checkLocation); clearInterval(interval); };
  }, []);

  useEffect(() => {
    async function fetchRole(userId: string) {
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", userId).single();
      setUserRole(data?.role ?? null);
    }
    supabase.auth.getSession().then(({ data }) => {
      if (data.session?.user) fetchRole(data.session.user.id);
      else setUserRole(null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, s) => {
      if (s?.user) fetchRole(s.user.id);
      else setUserRole(null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Only show on admin page for super_admin or manager
  if (!isOnAdminPage || !userRole || (userRole !== "super_admin" && userRole !== "manager")) return null;

  const handleSave = async () => {
    setShowMenu(false);
    await manualSave();
    setJustSaved(true);
    setTimeout(() => setJustSaved(false), 2000);
  };

  const handleLoad = async () => {
    setShowMenu(false);
    await manualLoad();
  };


  const handleForceSyncAll = async () => {
    setShowMenu(false);
    const result = await forceSyncAll();
    if (result.success) {
      await logSyncAudit("force synced", `Force synced ${result.syncedKeys.length} key(s)`);
      setForceSyncLog(result.syncedKeys.map(prettySyncKey));
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {showMenu && (
        <div className="bg-card border border-border rounded-xl shadow-lg p-2 space-y-1 animate-in slide-in-from-bottom-2 min-w-[180px]">
          <button
            onClick={handleSave}
            disabled={isSyncing}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg hover:bg-accent text-foreground transition-colors disabled:opacity-50"
          >
            {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save to Cloud
          </button>
          <button
            onClick={handleLoad}
            disabled={isSyncing}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg hover:bg-accent text-foreground transition-colors disabled:opacity-50"
          >
            {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Load from Cloud
          </button>
          {userRole === "super_admin" && (
            <button
              onClick={handleForceSyncAll}
              disabled={isSyncing}
              className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg hover:bg-accent text-foreground transition-colors disabled:opacity-50 border-t border-border mt-1 pt-2"
            >
              {isSyncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Cloud className="w-3.5 h-3.5" />}
              Force Sync All
            </button>
          )}
        </div>
      )}

      {/* Force Sync Log Panel */}
      {forceSyncLog && showSyncLog && (
        <div className="bg-card border border-border rounded-xl shadow-lg p-3 animate-in slide-in-from-bottom-2 min-w-[220px] max-w-[280px] max-h-[300px] flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-foreground flex items-center gap-1.5">
              <List className="w-3.5 h-3.5" />
              Synced Keys ({forceSyncLog.length})
            </span>
            <button onClick={() => setForceSyncLog(null)} className="text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto space-y-0.5 pr-1">
            {forceSyncLog.map((key, i) => (
              <div key={i} className="text-[10px] text-muted-foreground py-0.5 px-1.5 rounded bg-muted/50 truncate">
                {key}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toggle sync log visibility */}
      {forceSyncLog && (
        <button
          onClick={() => setShowSyncLog(!showSyncLog)}
          className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
        >
          <List className="w-3 h-3" />
          {showSyncLog ? "Hide log" : "Show log"}
        </button>
      )}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={`relative p-3 rounded-full shadow-lg transition-all ${
          justSaved
            ? "bg-primary text-primary-foreground"
            : isSyncing
              ? "bg-primary text-primary-foreground animate-pulse"
              : "bg-primary text-primary-foreground hover:bg-primary/90"
        }`}
        title={hasPendingChanges ? `${pendingCount} pending change(s)` : "Cloud Sync"}
      >
        {justSaved ? (
          <Check className="w-5 h-5" />
        ) : isSyncing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <Cloud className="w-5 h-5" />
        )}
        {hasPendingChanges && !isSyncing && (
          <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
            {pendingCount}
          </span>
        )}
      </button>
    </div>
  );
}
